import {Cart, Checkout, IAddressData, IGetCheckoutShippingOptionsData} from "@connox/typescript-library";
import i18n from "i18next";
import {isToNorthIreland} from "../../../checkout/steps/AddressStep/AddressStep";
import {positionsToDisplayItems} from "../googlepay/googlePayHelperFunctions";
import ApplePayLineItem = ApplePayJS.ApplePayLineItem;
import ApplePayShippingMethod = ApplePayJS.ApplePayShippingMethod;
import ApplePayDateComponentsRange = ApplePayJS.ApplePayDateComponentsRange;
import ApplePayShippingMethodSelectedEvent = ApplePayJS.ApplePayShippingMethodSelectedEvent;
import ApplePayCouponCodeChangedEvent = ApplePayJS.ApplePayCouponCodeChangedEvent;
import ApplePayCouponCodeUpdate = ApplePayJS.ApplePayCouponCodeUpdate;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ApplePayShippingContactUpdate = ApplePayJS.ApplePayShippingContactUpdate;
import ApplePayPaymentContact = ApplePayJS.ApplePayPaymentContact;
import braintree from "braintree-web";
import {INTERNAL_ERROR_TYPE, Payments} from "../PaymentController";
import {ErrorMagicAdditionalType} from "./ApplePayButtonLoader";
import {splitStreetAndNumber} from "../paypal/helpers/paypalAddressHelpers";
import {CartData} from "@connox/typescript-library/types/cart/CartData";

// Will be deleted on end of debugging and console.log removal!
/* eslint-disable no-console */

/**
 * Maps our shipping to the ones in `ApplePayShippingMethod` format
 * @param shippingOption
 * @param isNet
 */
export function mapShipping(shippingOption: IGetCheckoutShippingOptionsData, isNet: boolean): ApplePayShippingMethod {
  const {deliveryDaysMin, deliveryDaysMax} = shippingOption;

  const now = new Date();
  const firstThen = new Date(now.getFullYear(), now.getMonth(), now.getDate() + deliveryDaysMin);
  const lastThen = new Date(now.getFullYear(), now.getMonth(), now.getDate() + deliveryDaysMax);

  const dateComponentsRange: ApplePayDateComponentsRange = {
    startDateComponents: {
      years: firstThen.getFullYear(),
      months: firstThen.getMonth(),
      days: firstThen.getDate(),
      hours: 0,
    },
    endDateComponents: {
      years: lastThen.getFullYear(),
      months: lastThen.getMonth(),
      days: lastThen.getDate(),
      hours: 0,
    },
  };

  let amount = shippingOption.amount.amount;
  if (isNet) {
    amount = (Math.round(Number(shippingOption.amount.amount) / 0.0119) / 100).toString();
  }

  return {
    label: shippingOption.name,
    detail: shippingOption.description ?? "",
    amount,
    identifier: shippingOption.logisticsTypeId.toString(),
    dateComponentsRange,
  };
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778009-onshippingcontactselected
 * @param params {cart, checkout, session, shippingMethods}
 */
export async function asyncPutShippingCountry(params: {
  shippingContact: ApplePayPaymentContact;
  cart: Cart;
  checkout: Checkout | undefined;
  session: ApplePaySession;
  shippingMethods: ApplePayShippingMethod[] | undefined;
}) {
  console.log("shipping country selection started");
  if (!params.checkout) {
    throw new Error("No checkout object in putShippingCountry!");
  }

  const {countryCode, postalCode} = params.shippingContact;
  if (!countryCode || !postalCode || isToNorthIreland(countryCode, postalCode)) {
    console.log("countryCode or postalCode error:", countryCode || typeof countryCode, postalCode || typeof postalCode);
    params.session.completeShippingContactSelection(errorUpdate(params.cart));

    return;
  }

  let shippingOptions: IGetCheckoutShippingOptionsData[] | undefined;
  const differingCountryCodes = countryCode !== params.cart.countryCode;
  if (differingCountryCodes) {
    console.log("cart.put() on differing country codes");
    await params.cart.put({countryCode});
  }
  if (differingCountryCodes || !params.shippingMethods) {
    console.log(".getAvailableShippingOptions()");
    shippingOptions = await params.checkout.getAvailableShippingOptions();

    if (!shippingOptions.length) {
      // Maybe same, when there is an item with 'NOT_ALLOWED_FOR_COUNTRY'
      // then you don't need the .getAvailableShippingOptions() call
      console.log(
        "No shippingOptions!! Any item not shippable?:",
        params.cart.positions.find((pos) => pos.messages.some((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY"))?.text,
      );
      params.session.completeShippingContactSelection(errorUpdate(params.cart));

      return;
    }
  }

  const update: ApplePayShippingContactUpdate = {
    newTotal: {
      label: i18n.t("cart.total"),
      amount: params.cart.valuePayable.amount,
    },
    newLineItems: positionsToLineItems(params.cart),
  };

  if (shippingOptions) {
    update.newShippingMethods = shippingOptions.map((shippingOption: IGetCheckoutShippingOptionsData) =>
      mapShipping(shippingOption, params.cart.isNet),
    );
  }

  params.session.completeShippingContactSelection(update);
  console.log("Shipping country selection completed");

  return update.newShippingMethods;
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778028-onshippingmethodselected
 * @param event
 * @param checkout
 * @param session
 */
export async function asyncPutShippingMethod(
  event: ApplePayShippingMethodSelectedEvent,
  checkout: Checkout | undefined,
  session: ApplePaySession,
) {
  console.log("shipping method selection started");
  if (!checkout) {
    throw new Error("No checkout object in putShippingMethod!");
  }

  await checkout.put({logisticsTypeId: parseInt(event.shippingMethod.identifier, 10)});

  session.completeShippingMethodSelection({
    newTotal: {
      label: i18n.t("cart.total"),
      amount: checkout.cartData.value.payable.amount,
    },
    newLineItems: positionsToLineItems(checkout.cartData),
  });
  console.log("Shipping method selection completed");
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778013-onpaymentmethodselected
 * @param checkout
 * @param session
 */
export async function putPaymentMethod(checkout: Checkout | undefined, session: ApplePaySession) {
  console.log("payment method selection started");
  if (!checkout) {
    throw new Error("No checkout object in putPaymentMethod!");
  }

  if (checkout.paymentId !== Payments.ApplePay) {
    await checkout.put({paymentId: Payments.ApplePay});
  }

  session.completePaymentMethodSelection({
    newTotal: {
      label: i18n.t("cart.total"),
      amount: checkout.cartData.value.payable.amount,
    },
    newLineItems: positionsToLineItems(checkout.cartData),
  });
  console.log("Payment selection completed");
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/3813982-oncouponcodechanged
 * @param event
 * @param cart
 * @param session
 */
export async function asyncCouponCodeChange(
  event: ApplePayCouponCodeChangedEvent,
  cart: Cart,
  session: ApplePaySession,
) {
  await cart.addVoucher(event.couponCode);

  const vouchers = cart.positions.filter((pos) => pos.type === "Voucher");
  const errors: ApplePayError[] = [];
  vouchers.forEach((voucher) => {
    [...(voucher.reference?.voucher?.messages ?? []), ...voucher.messages].forEach((msg) =>
      errors.push(
        new ApplePayError(
          "couponCodeInvalid",
          undefined,
          i18n.t<string>(msg.message, {voucherCode: voucher.reference?.voucherCode}),
        ),
      ),
    );
  });

  const update: ApplePayCouponCodeUpdate = {
    newLineItems: positionsToLineItems(cart),
    newTotal: {
      label: i18n.t("cart.total"),
      amount: cart.valuePayable.amount,
    },
    errors: errors.length ? errors : undefined,
  };
  session.completeCouponCodeChange(update);
  console.log("Coupon code change completed");
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized
 * @param event
 * @param applePayInstance
 * @param session
 * @param doErrorMagic
 */
export async function asyncPaymentAuthorize(
  event: ApplePayPaymentAuthorizedEvent,
  applePayInstance: braintree.ApplePay,
  session: ApplePaySession,
  doErrorMagic: (
    error: braintree.BraintreeError | Error,
    errorNamePrefix: string,
    additional?: Record<string, ErrorMagicAdditionalType | undefined>,
  ) => void,
) {
  try {
    const tokenizedPayload = await applePayInstance.tokenize({token: event.payment.token});
    session.completePayment({status: ApplePaySession.STATUS_SUCCESS});
    console.log("Payment authorization completed");

    return tokenizedPayload.nonce;
  } catch (e) {
    doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnAuthorize, {
      event,
    });

    session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [new ApplePayError("unknown", undefined, "Error on payment tokenization")],
    });

    return "error";
  }
}

export function appleAddressAdapter(contact: ApplePayPaymentContact): IAddressData {
  const [street, streetNumber] = splitStreetAndNumber((contact?.addressLines ?? [""])[0], contact?.countryCode ?? "");

  return {
    firstName: contact.givenName ?? "",
    lastName: contact.familyName ?? "",
    zip: contact.postalCode ?? "",
    city: contact.locality ?? "",
    countryCode: contact.countryCode ?? "",
    state: contact.administrativeArea ?? "",
    phone: contact.phoneNumber ?? "",
    street,
    number: streetNumber,
    company: null,
    department: null,
    gender: "F",
    title: null,
    dateOfBirth: null,
    email: contact.emailAddress ?? "",
  };
}

export function positionsToLineItems(cart: Cart | CartData): ApplePayLineItem[] {
  return positionsToDisplayItems(cart).map(
    (pos: google.payments.api.DisplayItem): ApplePayLineItem => ({label: pos.label, amount: pos.price}),
  );
}

const errorUpdate = (cart: Cart): ApplePayShippingContactUpdate => ({
  errors: [
    new ApplePayError("addressUnserviceable", "countryCode", i18n.t<string>("checkout.noShippingToSelectedAddress")),
  ],
  newTotal: {
    label: i18n.t("cart.total"),
    amount: cart.valuePayable.amount,
  },
  newLineItems: positionsToLineItems(cart),
  newShippingMethods: [],
});
